import React from 'react';
import JSON5 from 'json5';
import styled from 'styled-components';
import TrueImpactText from '../base/TrueImpactText';
import { TrueImpactFile } from 'shared/design-system/components/TrueImpactFileUploadBox';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFile } from '@fortawesome/pro-light-svg-icons';
import { COLORS } from '../settings/variables/Colors';
import { SPACERS } from '../settings/variables/Spacers';
import { IndicatorDetails } from 'npo/pages/ReportBuilder/Components/IndicatorDetails/utils';
import {
  indicatorFileUploadInitialState,
} from 'npo/pages/ReportBuilder/Components/IndicatorDetails/IndicatorDetailQuestions/IndicatorFileUploadRender';

interface TrueImpactFileRenderProps {
  file: TrueImpactFile | string | undefined;
  style?: React.CSSProperties;
  className?: string;
}

const StyledDiv = styled.a`
  display: inline-flex;
  align-items: center;
  gap: ${SPACERS.tiny};
  color: ${COLORS.ctaPrimary};
  text-decoration: none;
  .icon {
    width: ${SPACERS.medium};
    height: ${SPACERS.medium};
  }
  .filename {
    color: inherit;
  }
  &:hover {
    text-decoration: underline;
  }
`;

const parseStringFiles = (file: string) => {
  if (!file || typeof file !== 'string') return [];
  try {
    const f = JSON5.parse(file) as ParsedFile[];
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return (JSON5.parse((f as any).fileName) as ParsedFile[]) || [];
    } catch (e) {
      return f || [];
    }
  } catch (e) {
    return [];
  }
};

type ParsedFile = {
  fileName: string;
  description: string | undefined;
  id: string | undefined;
  url: string;
};
export const parseFiles = (
  file: TrueImpactFile | string | undefined,
): ParsedFile[] => {
  if (typeof file === 'string') return parseStringFiles(file);
  if (!file || !file.fileName) return [];
  try {
    return JSON5.parse(file.fileName) as ParsedFile[];
  } catch (e) {
    if (!file.url) return [];
    return [
      {
        description: file.description,
        fileName: file.fileName,
        id: file.id?.toString(),
        url: file.url,
      },
    ];
  }
};

const TrueImpactFileRender: React.FC<TrueImpactFileRenderProps> = ({
  file,
  style,
  className,
}) => {
  let files = Array.isArray(file) ? file : parseFiles(file);
  if (files.length === 0) return null;
  if (typeof files !== 'string' && !Array.isArray(files)) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const f = (files as any).fileName;
    if (f && Array.isArray(f)) {
      files = f;
    } else {
      return null;
    }
  }
  return (
    <div className={className}>
      {files.map(f => {
        if (!f.url) return null;
        return (
          <StyledDiv
            key={f.id || f.fileName || f.url}
            style={style}
            href={f.url}
            target="_blank"
            rel="ugc"
            onClick={e => {
              e.stopPropagation();
            }}
          >
            <FontAwesomeIcon icon={faFile} className="icon" />
            <TrueImpactText className="filename">
              {f.fileName || f.url || 'Unknown'}
            </TrueImpactText>
          </StyledDiv>
        );
      })}
    </div>
  );
};

export const TrueImpactIndicatorFilesRender: React.FC<{
  indicator: IndicatorDetails;
}> = ({ indicator }) => {
  const files = indicatorFileUploadInitialState(indicator)();
  return files.length === 0 ? (
    <TrueImpactFileRender file={indicator.file} className="report-file" />
  ) : (
    <>
      {files.map((f, i) => (
        <TrueImpactFileRender
          key={`${indicator.indicatorId}-file-${i}`}
          file={f}
          className="report-file"
        />
      ))}
    </>
  );
};

export default TrueImpactFileRender;
